import { FC, Fragment, PropsWithChildren, Reducer, useEffect, useReducer } from 'react';
import { useMediaQuery } from '@mui/system';
import { useTranslation } from 'react-i18next';

import { CustomSnackbar } from '../../components/snackbar/snackbar';
import { theme } from '../../styles/theme';

import { ErrorSnackbarAction, ErrorSnackbarActionType, ErrorSnackbarState } from './types';
import { ErrorSnackbarContext } from './error-snackbar-context';

const ErrorSnackbarReducer: Reducer<ErrorSnackbarState, ErrorSnackbarAction> = (
    state: ErrorSnackbarState,
    action: ErrorSnackbarAction
) => {
    switch (action.type) {
        case ErrorSnackbarActionType.ERROR_BASKET_OUTDATED:
            return {
                open: true,
                message: 'basket_out_of_date_message',
                params: null,
                rebooker: false,
            };
        case ErrorSnackbarActionType.PROMOTION_EXPIRED:
            return {
                open: true,
                message: 'promotion_expired_message',
                params: null,
                rebooker: false,
            };
        case ErrorSnackbarActionType.ALREADY_HAS_BOOKING:
            return {
                open: true,
                message: 'user_already_booked',
                params: action.payload,
                rebooker: false,
            };
        case ErrorSnackbarActionType.BASE_PRICE_MISMATCH:
            return {
                open: true,
                message: 'room_price_updated_message',
                params: null,
                rebooker: false,
            };
        case ErrorSnackbarActionType.ERROR_APPLY_REFERRAL_CODE:
            return {
                open: true,
                message: 'vita_collective_code_add_to_basket_error',
                params: null,
                rebooker: false,
            };
        case ErrorSnackbarActionType.REBOOKER_PRICE_CHANGED:
            return {
                open: true,
                message: 'rebooker_price_changed_message',
                params: action.payload,
                rebooker: true,
            };
        case ErrorSnackbarActionType.ERROR_APPLY_REFERRAL_CODE_HOS:
            return {
                open: true,
                message: 'referral_code_add_to_basket_error',
                params: null,
                rebooker: false,
            };
        case ErrorSnackbarActionType.ERROR_CLEAR_REFERRAL_CODE:
            return {
                open: true,
                message: 'vita_collective_code_remove_from_basket_error',
                params: null,
                rebooker: false,
            };
        case ErrorSnackbarActionType.ERROR_CLEAR_REFERRAL_CODE_HOS:
            return {
                open: true,
                message: 'referral_code_remove_from_basket_error',
                params: null,
                rebooker: false,
            };
        case ErrorSnackbarActionType.CLOSE_SNACKBAR:
            return { open: false, message: '', params: null, rebooker: false };
        case ErrorSnackbarActionType.SOMETHING_WENT_WRONG:
            return { open: true, message: 'something_went_wrong', params: null, rebooker: false };
        default:
            return state;
    }
};

export const ErrorSnackbarProvider: FC<PropsWithChildren> = ({ children }) => {
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [{ open, message, params, rebooker }, dispatchError] = useReducer(ErrorSnackbarReducer, {
        open: false,
        message: '',
        params: null,
        rebooker: false,
    });

    useEffect(() => {
        if (open) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [open]);

    return (
        <Fragment>
            <ErrorSnackbarContext.Provider value={dispatchError}>
                {children}
            </ErrorSnackbarContext.Provider>
            <CustomSnackbar
                open={open}
                duration={8}
                onClose={() =>
                    dispatchError({ type: ErrorSnackbarActionType.CLOSE_SNACKBAR, payload: null })
                }
                message={t(message, params as Record<string, string>)}
                isSmallScreen={isSmallScreen}
                rebooker={rebooker}
            />
        </Fragment>
    );
};
